import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ListOptions } from '@capturum/api';
import { responseData } from '@capturum/builders/core';
import { MapItem } from '@capturum/ui/api';
import { ExtendedListOptions } from '@core/interfaces/api.interface';
import { ApplicationAction } from '@features/applications/interfaces/action.interface';
import {
  Application,
  ApplicationAuthTestConnectionResponse,
  AuthenticationParameter,
  AuthenticationParameterBody,
} from '@features/applications/interfaces/application.interface';
import { MaintenancePayload, MaintenanceState } from '@shared/interfaces/maintenance.interface';
import { map, Observable, of } from 'rxjs';
import { EntityApiService } from './entity-api.service';

@Injectable({
  providedIn: 'root',
})
export class ApplicationApiService extends EntityApiService<Application> {
  protected override endpoint: string = 'application';

  private http = inject(HttpClient);

  public getApplicationActions(applicationId: string): Observable<ApplicationAction[]> {
    return this.apiHttp.get<{ data: ApplicationAction[] }>(`/application/${applicationId}/action`).pipe(responseData);
  }

  public getApplicationActionById(
    applicationId: string,
    actionId: string,
    listOptions?: ListOptions,
  ): Observable<{ data: ApplicationAction }> {
    const url = `/application/${applicationId}/action/${actionId}${this.getOptionsQuery(listOptions)}`;

    return this.apiHttp.get(url);
  }

  public getAuthenticationParameters(applicationId: string): Observable<{ authentificated: boolean }> {
    return this.apiHttp.get(`/${this.endpoint}/${applicationId}/authentication-parameter`);
  }

  public createAuthenticationParameters(
    applicationId: string,
    body: AuthenticationParameterBody,
    ...params: (string | number)[]
  ): Observable<AuthenticationParameter> {
    return this.apiHttp
      .post<{ data: AuthenticationParameter }>(`/${this.endpoint}/${applicationId}/authentication-parameter`, body)
      .pipe(responseData);
  }

  public updateAuthenticationParameters(
    applicationId: string,
    body: AuthenticationParameterBody,
    parameterId: string | number,
  ): Observable<AuthenticationParameter> {
    return this.apiHttp
      .put<{
        data: AuthenticationParameter;
      }>(`/${this.endpoint}/${applicationId}/authentication-parameter/${parameterId}`, body)
      .pipe(responseData);
  }

  public deleteAuthenticationParameters(applicationId: string, paremeterId: string | number): Observable<void> {
    return this.apiHttp.delete(`/${this.endpoint}/${applicationId}/authentication-parameter/${paremeterId}`);
  }

  public testConnection(
    applicationId: string,
    options?: ListOptions,
  ): Observable<ApplicationAuthTestConnectionResponse> {
    return this.apiHttp.get(`/${this.endpoint}/${applicationId}/test-authentication${this.getOptionsQuery(options)}`);
  }

  public setAppMaintenance(
    id: string | number,
    state: MaintenanceState,
    payload?: MaintenancePayload,
  ): Observable<boolean> {
    switch (state) {
      case 'on':
        return of(true);
      // @Todo: BE doesn't have this endpoint yet
      // return this.apiHttp.patch<boolean>(`/${this.endpoint}/${id}/maintenance-on`, payload);
      case 'off':
        return of(true);
      // @Todo: BE doesn't have this endpoint yet
      // return this.apiHttp.patch<boolean>(`/${this.endpoint}/${id}/maintenance-off`, {});
      default:
        throw new Error('Invalid state');
    }
  }

  public getSecretsList(options?: ExtendedListOptions): Observable<MapItem[]> {
    return this.apiHttp.get<{ data: MapItem[] }>(`/secret/list${this.generateExtendedQuery(options)}`).pipe(
      map((response) => {
        return response.data || [];
      }),
    );
  }

  public connectToApplication(url: string): Observable<unknown> {
    const headers = new HttpHeaders({
      'Content-Type': 'text/html; charset=utf-8',
      Accept: 'text/html, application/json, */*;q=0.8',
    });

    return this.http.get(url, {
      headers,
      responseType: 'text',
    });
  }

  public connectClientCredentials(applicationId: string | number): Observable<void> {
    return this.apiHttp.post(`/${this.endpoint}/${applicationId}/connect`, {});
  }

  public disconnectApplication(applicationId: string): Observable<void> {
    return this.apiHttp.get(`/${this.endpoint}/${applicationId}/disconnect`);
  }

  connectApplicationWithClientCredentials(applicationId: string): Observable<void> {
    return this.apiHttp.post(`/${this.endpoint}/${applicationId}/connect`, {});
  }
}
